// bully ignore[directory-kebab-case: component-directory-required requires this directory to literally equal the PascalCase file stem (LatestFindingsCell), and no rule enforces PascalCase .tsx filenames — kebab-casing both dir and file instead trips oxlint's unicorn/filename-case; no shape satisfies all three checks at once. follow-up: revisit if bully's directory-kebab-case gains a component-directory exemption.]
import React from 'react'
import styled from 'styled-components'
import { Popover } from 'sutra/components/Popover/Popover'

import {
  AgentRunSpinner,
  AutoplayIcon,
} from 'features/trend-detection/components/AgentActivityIcons'
import {
  BlueDot,
  Column,
  IncompleteTrigger,
  MainLine,
  PopoverBody,
  PopoverText,
  RetryButton,
  SubLine,
  WarningIcon,
} from 'features/trend-detection/features/agent-list/components/LatestFindingsCell/styles'
import { formatRelativeTime } from 'features/trend-detection/utils/helpers'

const RUN_INCOMPLETE_BODY_OWNER =
  "This agent couldn't complete its latest run, so no new insights were added. Retry the run now, or wait for the next scheduled run."

const RUN_INCOMPLETE_BODY_VIEWER =
  "This agent couldn't complete its run, so no new insights were added. Check more recent activity for newer insights."

export type AgentRunStatus = 'created' | 'running' | 'completed' | 'failed'

export type LatestFindingsCellProps = {
  runStatus: AgentRunStatus | null
  insightCount: number
  isPaused: boolean
  lastRunTime: string | null
  onRerun?: () => void
}

export const LatestFindingsCell: React.FC<LatestFindingsCellProps> = ({
  runStatus,
  insightCount,
  isPaused,
  lastRunTime,
  onRerun,
}) => {
  // Paused agents are filtered out of the feed, so any "N insights" copy or
  // deep-link CTA would lead nowhere. The Status pill already surfaces the
  // paused state; leave this cell blank so the row's right side doesn't
  // dangle broken affordances. Users see run health via the detail page.
  if (isPaused) return null

  // 'created' is the pre-Prefect-pickup state — UX-identical to 'running'
  // (run dispatched, insights pending). Merged here so a just-created agent
  // doesn't flash "Not yet run" while the BE hands off to Prefect.
  if (runStatus === 'running' || runStatus === 'created') {
    return (
      <Column>
        <MainLine>
          <AgentRunSpinner />
          Running now
        </MainLine>
        <SubLine>Insights pending</SubLine>
      </Column>
    )
  }

  if (runStatus === 'failed') {
    return (
      <Column>
        <MainLine>No new insights</MainLine>
        <SubLine>
          <FindingsPopover
            openOnHover
            content={
              <PopoverBody>
                <PopoverText>
                  {onRerun
                    ? RUN_INCOMPLETE_BODY_OWNER
                    : RUN_INCOMPLETE_BODY_VIEWER}
                </PopoverText>
                {onRerun ? (
                  <RetryButton
                    type="button"
                    onClick={(e) => {
                      // Popover portals content but React events still bubble
                      // through the virtual DOM to the row — without this the
                      // Retry click also navigates to the detail page.
                      e.stopPropagation()
                      onRerun()
                    }}
                  >
                    <AutoplayIcon />
                    Retry run
                  </RetryButton>
                ) : null}
              </PopoverBody>
            }
          >
            <IncompleteTrigger
              type="button"
              onClick={(e) => e.stopPropagation()}
            >
              <WarningIcon />
              Run incomplete
            </IncompleteTrigger>
          </FindingsPopover>
        </SubLine>
      </Column>
    )
  }

  if (runStatus === 'completed') {
    if (insightCount > 0) {
      return (
        <Column>
          <MainLine>
            <BlueDot />
            {insightCount} new {insightCount === 1 ? 'insight' : 'insights'}
          </MainLine>
          <SubLine>
            {lastRunTime ? formatRelativeTime(lastRunTime) : null}
          </SubLine>
        </Column>
      )
    }
    return (
      <Column>
        <MainLine>No new insights</MainLine>
        <SubLine>
          {lastRunTime ? `Ran ${formatRelativeTime(lastRunTime)}` : null}
        </SubLine>
      </Column>
    )
  }

  // runStatus is null — no run history. ('created' merges with 'running' above.)
  return (
    <Column>
      <MainLine>Not yet run</MainLine>
    </Column>
  )
}

const FindingsPopover = styled(Popover)`
  max-width: 292px;
`
